/**
 * Central User Registration Utility
 *
 * THIS MODULE MUST BE CALLED BY ALL APPS after OAuth signup!
 *
 * Usage in each app's OAuthCallback:
 *
 * import { registerUserForApp } from './utils/centralRegistration';
 *
 * useEffect(() => {
 *   if (isAuthenticated && user) {
 *     registerUserForApp(user, 'invoicer').then(() => {
 *       navigate('/dashboard');
 *     });
 *   }
 * }, [isAuthenticated, user]);
 */

const CENTRAL_API = process.env.REACT_APP_CENTRAL_API_URL || 'https://us-central1-paymentgateway-ab783.cloudfunctions.net/api';

/**
 * Register user for an app in the central system
 * This triggers:
 * - User document creation/update in central Firestore
 * - App registration record
 * - Welcome email + onboarding sequence
 *
 * @param {Object} user - Firebase user object
 * @param {string} appId - App identifier (e.g., 'invoicer', 'ai-caller', 'payroll-system')
 * @param {Object} options - Optional configuration
 * @returns {Promise<Object>} Registration result
 */
export async function registerUserForApp(user, appId, options = {}) {
  try {
    // Check if already registered (using localStorage to avoid duplicate calls)
    const registrationKey = `registered_${appId}_${user.uid}`;
    if (localStorage.getItem(registrationKey)) {
      console.log(`✅ User already registered for ${appId}`);
      return { success: true, alreadyRegistered: true };
    }

    console.log(`📝 Registering user ${user.email} for app ${appId}...`);

    // Get Firebase ID token
    const token = await user.getIdToken();

    // Call central registration endpoint
    const response = await fetch(`${CENTRAL_API}/multi-app-users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        appId,
        initialPlan: options.initialPlan || 'free',
        profileData: {
          firstName: options.firstName || user.displayName?.split(' ')[0] || '',
          lastName: options.lastName || user.displayName?.split(' ').slice(1).join(' ') || '',
          ...options.profileData,
        },
        registrationContext: {
          source: 'oauth-signup',
          userAgent: navigator.userAgent,
          ...options.registrationContext,
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }

    const result = await response.json();

    if (result.success) {
      // Mark as registered in localStorage
      localStorage.setItem(registrationKey, Date.now().toString());
      console.log(`✅ Successfully registered user for ${appId}`);
      console.log(`📧 Welcome email sent: ${result.coreUser?.email}`);
    }

    return result;

  } catch (error) {
    console.error(`❌ Failed to register user for ${appId}:`, error);

    // Don't block user flow even if registration fails
    // Log to Sentry/analytics here if available

    return {
      success: false,
      error: error.message,
      // Still allow user to proceed
      allowAccess: true,
    };
  }
}

/**
 * Check if user is registered for an app
 * @param {Object} user - Firebase user object
 * @param {string} appId - App identifier
 * @returns {Promise<boolean>} Whether user is registered
 */
export async function isUserRegistered(user, appId) {
  try {
    const token = await user.getIdToken();

    const response = await fetch(`${CENTRAL_API}/multi-app-users/me/apps/${appId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.appUser !== null;
    }

    return false;
  } catch (error) {
    console.error('Failed to check registration status:', error);
    return false;
  }
}

/**
 * Get all apps user is registered with
 * @param {Object} user - Firebase user object
 * @returns {Promise<Array>} List of registered apps
 */
export async function getUserApps(user) {
  try {
    const token = await user.getIdToken();

    const response = await fetch(`${CENTRAL_API}/multi-app-users/me/apps`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.apps || [];
    }

    return [];
  } catch (error) {
    console.error('Failed to get user apps:', error);
    return [];
  }
}

export default {
  registerUserForApp,
  isUserRegistered,
  getUserApps,
};
